import { useGetFastVarsQuery } from "@api/fast-versioning";
import type { IFsVariable } from "@/shared/types";
import { useMemo } from "react";
import { canLandInlineInText } from "../insert/variable-scope";
import { useHubspotCategories } from "../variable-picker/hooks/use-hubspot-categories";
import { usePickerTabs } from "../variable-picker/hooks/use-picker-tabs";
import { usePlanLock } from "../variable-picker/hooks/use-plan-lock";
import { PICKER_CONTENT } from "../variable-picker/picker-content";
import type {
  PickerVariable,
  PlanTier,
  VariableValueType,
} from "../variable-picker/types";

// Mirrors CustomTab's fast-version → picker type mapping (custom-tab.tsx) —
// duplicated rather than imported so this hook doesn't reach into a component
// module; keep the two in sync if the fast-version type list changes.
const FS_TYPE_MAP: Record<string, VariableValueType> = {
  input: "input",
  "editor-basic": "input",
  image: "image",
  video: "video",
  "image-video": "media",
  table: "data",
  calc: "ai",
  list: "list",
  number: "number",
  date: "date",
  color: "color",
  media: "media",
  select: "select",
  multiselect: "multiselect",
  ai: "ai",
  data: "data",
  iframe: "iframe",
  calculation: "calculation",
};

const toCustomVariables = (fsVars: IFsVariable[]): PickerVariable[] =>
  fsVars
    .filter(
      (v) =>
        !v.archived &&
        !v.variable?.name?.startsWith("hubspot.") &&
        !v.variable?.parentId,
    )
    .map((v) => ({
      id: v._id,
      name: v.variable.title || v.variable.name,
      token:
        v.variable.type === "editor-basic"
          ? `{{{${v.variable.name}}}}`
          : `{{${v.variable.name}}}`,
      type: FS_TYPE_MAP[v.variable.type] ?? "input",
      // Calculation variables require Team; every other custom variable
      // requires Pro (mirrors custom-tab.tsx's identical mapping). "calculation"
      // isn't (yet) a member of IFsVariableTypeEnum — see use-calc-formulas.ts.
      plan:
        (v.variable.type as unknown as string) === "calculation"
          ? ("team" as const)
          : ("pro" as const),
    }));

/**
 * Only variables that may land as an inline token inside text — the same
 * rule drag-and-drop applies to a RichText target (variable-scope.ts). An
 * image, video, iframe, data or colour variable binds to its own element, so
 * picking one from the `{{` popup would plant a token the text can never
 * resolve; the formula editor (the other consumer) has no use for them either.
 */
export const onlyInlineInsertable = (
  variables: PickerVariable[],
): PickerVariable[] =>
  variables.filter((variable) => canLandInlineInText(variable.type));

/**
 * Stable partition putting the variables the account can actually insert
 * first and the plan-locked ones last, so a Starter/Pro account isn't shown
 * a list whose top rows all open the Upgrade dialog. Both halves keep their
 * incoming order, so the Storydoc → HubSpot → Fathom → Custom grouping (and
 * each source's own order) survives within them.
 *
 * `isLocked` is injected — the caller passes `usePlanLock().isPlanLocked`,
 * the exact predicate `VariableRow` uses to decide whether a row shows the
 * crown chip, so the ordering can never disagree with the rows it orders.
 */
export const lockedLast = (
  variables: PickerVariable[],
  isLocked: (plan?: PlanTier) => boolean,
): PickerVariable[] => {
  const available: PickerVariable[] = [];
  const locked: PickerVariable[] = [];
  for (const variable of variables) {
    if (isLocked(variable.plan)) locked.push(variable);
    else available.push(variable);
  }
  return locked.length === 0 ? available : [...available, ...locked];
};

/**
 * The same cross-category variable set the docked VariablePicker builds
 * (Storydoc + Custom + HubSpot/Fathom when connected), flattened into one
 * list for the inline `{{` typeahead — which shows a flat, tab-less list per
 * the Figma spec. Reuses the picker's own hooks/content so there is exactly
 * one place each data source is fetched from.
 */
export const useTypeaheadVariables = (): {
  variables: PickerVariable[];
  isLoading: boolean;
} => {
  const tabs = usePickerTabs();
  const hasHubspot = tabs.some((t) => t.id === "hubspot");
  const hasFathom = tabs.some((t) => t.id === "fathom");
  const { isPlanLocked } = usePlanLock();

  const { categories: hubspotCategories } = useHubspotCategories(hasHubspot);
  const {
    data: fastVarsData,
    isLoading: isCustomLoading,
    isUninitialized: isCustomUninitialized,
  } = useGetFastVarsQuery(undefined);

  const variables = useMemo(() => {
    const storydocContent = PICKER_CONTENT.storydoc;
    const storydoc =
      storydocContent?.kind === "categories"
        ? storydocContent.categories.flatMap((c) => c.variables)
        : [];

    const fathomContent = PICKER_CONTENT.fathom;
    const fathom =
      hasFathom && fathomContent?.kind === "flat" ? fathomContent.variables : [];

    const hubspot = hasHubspot
      ? hubspotCategories.flatMap((c) => c.variables)
      : [];

    const custom = toCustomVariables((fastVarsData ?? []) as IFsVariable[]);

    return lockedLast(
      onlyInlineInsertable([...storydoc, ...hubspot, ...fathom, ...custom]),
      isPlanLocked,
    );
  }, [hasFathom, hasHubspot, hubspotCategories, fastVarsData, isPlanLocked]);

  const isLoading =
    isCustomLoading ||
    isCustomUninitialized ||
    (hasHubspot && hubspotCategories.some((c) => c.isLoading));

  return { variables, isLoading };
};
